import { useCallback } from 'react';

import { BffApiUpdateItemError, UpdateItemGqlStatus } from '../../../../types';

import {
  InvoiceID,
  InvoiceNanoID,
  InvoicePaymentDate,
  InvoicePaymentMethods,
  InvoiceUUID,
  PayInvoiceErrorCacheKeys
} from '../../invoicesTypes';

import { CACHE_INVOICE_PAY_QUERY } from '../../queries/cacheInvoicePay.query';
import { CACHE_INVOICE_PAY_ASYNC_QUERY } from '../../queries/cacheInvoicePayAsync.query';

import { useCacheUpdateQuery } from '../../../common/reactQuery/hooks/useCacheUpdateQuery';

interface CacheInvoicePayOptions {
  cacheKeys?: PayInvoiceErrorCacheKeys;
}

export interface CacheInvoicePayInvoice {
  paymentMethod: InvoicePaymentMethods;
  paymentDate?: InvoicePaymentDate;
  confirmationId?: string;
  paid?: number;
  received?: number;
  transactionFeeExpense?: number;
}

export type CacheInvoicePayInput = CacheInvoicePayInvoice & {
  uuid: InvoiceID | InvoiceNanoID | InvoiceUUID;
};

export interface CacheInvoicePayHandlerInput {
  invoiceId: InvoiceID;
  async?: boolean;
  invoice: CacheInvoicePayInvoice;
}

export interface CacheInvoicePayResponse {
  invoicePay: {
    status: UpdateItemGqlStatus;
  };
}

export interface CacheInvoicePayAsyncResponse {
  invoicePayAsync: {
    status: UpdateItemGqlStatus;
  };
}

export type CacheInvoicePayError = BffApiUpdateItemError;

const action = 'invoicePay';
const asyncAction = 'invoicePayAsync';

function useCacheInvoicePay<CacheInvoicePayRecordType = unknown>({
  cacheKeys
}: CacheInvoicePayOptions = {}) {
  const {
    updateCacheQuery: payInvoice,
    updateCacheQueryErrorMessage: payInvoiceErrorMessage,
    updateCacheQueryLoading: payInvoiceLoading,
    updateCacheQueryReset: payInvoiceReset
  } = useCacheUpdateQuery<
    CacheInvoicePayInput,
    CacheInvoicePayResponse,
    CacheInvoicePayError,
    CacheInvoicePayRecordType
  >({ action, query: CACHE_INVOICE_PAY_QUERY, cacheKeys });

  const {
    updateCacheQuery: payInvoiceAsync,
    updateCacheQueryErrorMessage: payInvoiceAsyncErrorMessage,
    updateCacheQueryLoading: payInvoiceAsyncLoading,
    updateCacheQueryReset: payInvoiceAsyncReset
  } = useCacheUpdateQuery<
    CacheInvoicePayInput,
    CacheInvoicePayAsyncResponse,
    CacheInvoicePayError,
    CacheInvoicePayRecordType
  >({
    action: asyncAction,
    query: CACHE_INVOICE_PAY_ASYNC_QUERY,
    cacheKeys
  });

  const handlePayInvoice = useCallback<
    (input: CacheInvoicePayHandlerInput) => void
  >(
    (input) => {
      const mutate = input.async ? payInvoiceAsync : payInvoice;

      mutate({ uuid: input.invoiceId, ...input.invoice });
    },
    [payInvoice, payInvoiceAsync]
  );

  const handlePayInvoiceReset = useCallback<() => void>(() => {
    payInvoiceReset();
    payInvoiceAsyncReset();
  }, [payInvoiceAsyncReset, payInvoiceReset]);

  return {
    payInvoiceErrorMessage:
      payInvoiceErrorMessage || payInvoiceAsyncErrorMessage,
    payInvoiceLoading: payInvoiceLoading || payInvoiceAsyncLoading,
    payInvoiceReset: handlePayInvoiceReset,
    handlePayInvoice
  };
}

export default useCacheInvoicePay;
